import { ArrowRight, CalendarDays } from "lucide-react";
import { Link } from "wouter";
import { SITE, trackEvent } from "@/lib/site";

const teaserCopy =
  "Legend tells of a poor fisherman by the Tonlé Sap who had nothing to offer his king but his morning's catch. He steamed it in banana leaf with fragrant kroeung and coconut, and the king asked for more. Today Fish Amok is Cambodia's signature dish, and ours.";

export function AmokLegendSection() {
  return (
    <section
      className="amok-legend"
      id="legend"
      aria-labelledby="amok-legend-heading"
    >
      <div className="amok-legend-rule" aria-hidden="true" />
      <p className="eyebrow amok-legend-eyebrow">As the story goes</p>
      <h2 id="amok-legend-heading">The legend of Amok</h2>
      <div className="amok-legend-body">
        <p>
          Long ago, in the time of the great kings of Angkor, a poor fisherman
          lived beside the Tonlé Sap lake. He had a small boat, a worn net and
          very little else. When word came that the king would pass through the
          villages, every family hurried to prepare a gift. The rich brought
          gold, silk and fine carvings. The fisherman had only his morning's
          catch.
        </p>
        <p>
          Not wanting to bow before his king with a plain fish, he went to his
          wife's kitchen and began to work. He pounded lemongrass, galangal,
          turmeric, kaffir lime and garlic into a fragrant kroeung, the way his
          mother had taught him. He folded it through coconut milk with the
          tender white fish, shaped banana leaves into little cups and steamed
          them gently over the fire until the curry set soft and silky.
        </p>
        <p>
          When the king arrived, the fisherman knelt and offered one small
          banana-leaf cup. The courtiers laughed at so humble a gift. The king
          tasted it, fell silent, and asked for another. "What is this?" he
          said. "Only fish from the lake," the fisherman answered, "cooked with
          what our land has given us."
        </p>
        <p>
          The king declared it fit for a royal table. From that day the
          fisherman's dish was cooked in the palace, and his recipe travelled
          from kitchen to kitchen, from mothers to daughters, across Cambodia.
        </p>
        <p>
          Today, Fish Amok is Cambodia's signature dish, and ours. At ANGKOR it
          is a fragrant steamed coconut curry made with aromatic kroeung,
          prepared with the same care as a gift for a king.
        </p>
      </div>
      <p className="amok-legend-note">
        <em>
          Amok's true origins are debated, and this is a folk legend as it is
          often told, not recorded history.
        </em>
      </p>
      <div className="inline-actions amok-legend-actions">
        <a
          className="button button-gold"
          href={SITE.booking}
          onClick={() =>
            trackEvent("book_table_click", { placement: "fish_amok_legend" })
          }
        >
          Book a Table <CalendarDays size={17} />
        </a>
        <Link className="button button-dark" href="/menu">
          View menu <ArrowRight size={17} />
        </Link>
      </div>
    </section>
  );
}

type AmokLegendTeaserProps = {
  headingLevel: "h2" | "h3";
  placement: "home_signature" | "our_story";
};

export function AmokLegendTeaser({
  headingLevel: Heading,
  placement,
}: AmokLegendTeaserProps) {
  const headingId = `amok-legend-teaser-${placement}`;

  return (
    <aside className="amok-legend-teaser" aria-labelledby={headingId}>
      <div className="amok-legend-rule" aria-hidden="true" />
      <Heading id={headingId}>A dish fit for a king</Heading>
      <p>{teaserCopy}</p>
      <Link
        className="text-link amok-legend-link"
        href="/fish-amok-melbourne#legend"
        onClick={() => trackEvent("amok_legend_click", { placement })}
      >
        Read the legend of Amok <ArrowRight size={16} />
      </Link>
    </aside>
  );
}
